import React, { useState, useMemo } from 'react';
import { X, AlertTriangle, Trash2, Clock, CheckCircle, XCircle } from 'lucide-react';
import { FileMetadata, FolderStructure } from '../../../shared/types';
import { formatFileSize, formatDate } from '../utils/formatters';

interface DeletionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  selectedFiles: FileMetadata[];
  selectedFolders: FolderStructure[];
  isDeleting: boolean;
}

interface DeletionEligibility {
  canDelete: boolean;
  reason?: string;
  daysUntilEligible?: number;
}

export const DeletionConfirmationModal: React.FC<DeletionConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedFiles,
  selectedFolders,
  isDeleting
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // Calculate deletion eligibility for Archive Tier files
  const calculateDeletionEligibility = (file: FileMetadata): DeletionEligibility => {
    if (file.storageTier === 'upload') {
      return { canDelete: true };
    }

    // For Archive Tier, check 120-day eligibility
    if (file.storageTier === 'archive' && file.archivalDate) {
      const archivalDate = new Date(file.archivalDate);
      const now = new Date();
      const daysSinceArchival = Math.floor((now.getTime() - archivalDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysSinceArchival >= 120) {
        return { canDelete: true };
      } else {
        const daysUntilEligible = 120 - daysSinceArchival;
        return {
          canDelete: false,
          reason: `File must remain in Archive Tier for 120 days before deletion. ${daysUntilEligible} days remaining.`,
          daysUntilEligible
        };
      }
    }

    // If no archival date but in archive tier, assume not eligible
    if (file.storageTier === 'archive') {
      return {
        canDelete: false,
        reason: 'Archival date not available. Cannot determine deletion eligibility.'
      };
    }

    return { canDelete: true };
  };

  // Calculate folder deletion eligibility based on contained files
  const calculateFolderEligibility = (folder: FolderStructure): DeletionEligibility => {
    if (folder.folderPath.startsWith('/upload')) {
      return { canDelete: true };
    }

    // For Archive Tier folders, we need to check if all files would be eligible
    // Since we don't have the files here, we'll assume they need to be checked server-side
    if (folder.folderPath.startsWith('/archive')) {
      return {
        canDelete: true, // Server will validate individual files
        reason: 'All files in this folder must meet the 120-day eligibility requirement.'
      };
    }

    return { canDelete: true };
  };

  // Analyze all selected items
  const deletionAnalysis = useMemo(() => {
    const fileAnalysis = selectedFiles.map(file => ({
      item: file,
      type: 'file' as const,
      eligibility: calculateDeletionEligibility(file)
    }));

    const folderAnalysis = selectedFolders.map(folder => ({
      item: folder,
      type: 'folder' as const,
      eligibility: calculateFolderEligibility(folder)
    }));

    const allItems = [...fileAnalysis, ...folderAnalysis];
    const ineligibleItems = allItems.filter(item => !item.eligibility.canDelete);
    const eligibleItems = allItems.filter(item => item.eligibility.canDelete);

    return {
      allItems,
      eligibleItems,
      ineligibleItems,
      canProceed: ineligibleItems.length === 0 && allItems.length > 0,
      hasArchiveItems: allItems.some(item => 
        (item.type === 'file' && (item.item as FileMetadata).storageTier === 'archive') ||
        (item.type === 'folder' && (item.item as FolderStructure).folderPath.startsWith('/archive'))
      )
    };
  }, [selectedFiles, selectedFolders]);

  const handleConfirm = async () => {
    if (deletionAnalysis.canProceed) {
      await onConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Confirm Deletion
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="mt-4">
          {/* Summary */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              You are about to delete{' '}
              <span className="font-medium text-gray-900">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
              </span>
              {selectedFiles.length > 0 && selectedFolders.length > 0 ? ' and ' : ''}
              {selectedFolders.length > 0 && (
                <span className="font-medium text-gray-900">
                  {selectedFolders.length} folder{selectedFolders.length !== 1 ? 's' : ''}
                </span>
              )}
              . This action cannot be undone.
            </p>
          </div>

          {/* Eligibility Status */}
          {deletionAnalysis.hasArchiveItems && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start space-x-2">
                <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Archive Tier Deletion Policy</p>
                  <p className="text-sm text-blue-800 mt-1">
                    Files in Archive Tier must remain stored for at least 120 days before they can be deleted.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Ineligible Items Warning */}
          {deletionAnalysis.ineligibleItems.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start space-x-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">
                    {deletionAnalysis.ineligibleItems.length} item{deletionAnalysis.ineligibleItems.length !== 1 ? 's' : ''} cannot be deleted
                  </p>
                  <p className="text-sm text-red-800 mt-1">
                    Some selected items do not meet the deletion requirements.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Eligible Items Confirmation */}
          {deletionAnalysis.eligibleItems.length > 0 && deletionAnalysis.canProceed && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    All selected items are eligible for deletion
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    {deletionAnalysis.eligibleItems.length} item{deletionAnalysis.eligibleItems.length !== 1 ? 's' : ''} will be permanently deleted.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Details Toggle */}
          <div className="mb-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>

          {/* Detailed Item List */}
          {showDetails && (
            <div className="mb-4 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
              <div className="divide-y divide-gray-200">
                {deletionAnalysis.allItems.map((item, index) => (
                  <div key={index} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <Trash2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {item.type === 'file' 
                              ? (item.item as FileMetadata).fileName
                              : (item.item as FolderStructure).concertName
                            }
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.type === 'file'
                              ? (item.item as FileMetadata).storageTier === 'upload'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                              : (item.item as FolderStructure).folderPath.startsWith('/upload')
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                          }`}>
                            {item.type === 'file'
                              ? (item.item as FileMetadata).storageTier === 'upload' ? 'Upload' : 'Archive'
                              : (item.item as FolderStructure).folderPath.startsWith('/upload') ? 'Upload' : 'Archive'
                            }
                          </span>
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-500">
                          {item.type === 'file' ? (
                            <>
                              {formatFileSize((item.item as FileMetadata).sizeBytes)} • 
                              Uploaded {formatDate((item.item as FileMetadata).uploadDate)}
                              {(item.item as FileMetadata).archivalDate && (
                                <> • Archived {formatDate((item.item as FileMetadata).archivalDate!)}</>
                              )}
                            </>
                          ) : (
                            <>
                              {(item.item as FolderStructure).fileCount} files • 
                              {formatFileSize((item.item as FolderStructure).totalSizeBytes)} • 
                              Created {formatDate((item.item as FolderStructure).createdDate)}
                            </>
                          )}
                        </div>

                        {item.eligibility.reason && (
                          <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                            {item.eligibility.reason}
                          </div>
                        )}
                      </div>
                      
                      <div className="ml-4 flex-shrink-0">
                        {item.eligibility.canDelete ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!deletionAnalysis.canProceed || isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2 inline-block" />
                Delete {deletionAnalysis.eligibleItems.length} Item{deletionAnalysis.eligibleItems.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};